import { format } from 'date-fns'
import { v4 } from 'uuid'
import i18next from '@/translations/lang'

/**
 * Gera o script HTML/CSS/JS para um botão CTA
 * @param {Object} buttonConfig - Configuração do botão
 * @returns {string} Script gerado para o player
 */
export function generateInsideButtonScript(buttonConfig) {
  let btnAlignment
  let btnType = (buttonConfig.selectedButtonPlacement || '').toLowerCase()
  let btnQueries
  if (btnType.includes('left')) btnAlignment = 'justify-content:left'
  if (btnType.includes('center')) btnAlignment = 'justify-content:center'
  if (btnType.includes('right')) btnAlignment = 'justify-content:right'
  if (btnType.includes('top')) btnAlignment += ';align-items:start'
  if (btnType.includes('mid')) btnAlignment += ';align-items:center'
  if (btnType.includes('bottom')) btnAlignment += ';align-items:end'

  let btnMeasurements
  let btnSizeType = buttonConfig.selectedButtonSize
  if (btnSizeType === 'small')
    btnMeasurements = 'padding: 18px 44px;font-size:16px;'
  if (btnSizeType === 'medium')
    btnMeasurements = 'padding: 22px 66px;font-size:22px;'
  if (btnSizeType === 'large')
    btnMeasurements = 'padding: 26px 78px;font-size:26px;'

  let scriptMargin = 'margin: 75px 30px 30px 30px'
  if (btnType.includes('topleft')) scriptMargin = 'margin:30px'
  if (btnType.includes('topcenter')) scriptMargin = 'margin:30px'
  if (btnType.includes('topright')) scriptMargin = 'margin:30px'
  if (btnType.includes('midleft')) scriptMargin = 'margin: 0px 30px'
  if (btnType.includes('midcenter')) scriptMargin = 'margin: 0px'
  if (btnType.includes('midright')) scriptMargin = 'margin: 0px 30px'
  if (btnType.includes('bottomleft'))
    scriptMargin = 'margin: 75px 30px 30px 30px'
  if (btnType.includes('bottomcenter'))
    scriptMargin = 'margin: 75px 30px 30px 30px'
  if (btnType.includes('bottomright'))
    scriptMargin = 'margin: 75px 30px 30px 30px'

  // Verifica se é redirecionamento para vídeo ou link externo
  const isVideoRedirect =
    buttonConfig.redirectVideoId && buttonConfig.redirectVideoId.trim() !== ''

  let redirectFunction
  let buttonElement

  if (isVideoRedirect) {
    // Para redirecionamento de vídeo
    redirectFunction = `function redirect(){
      window.parent.postMessage({ type: "redirect_video", parameter: { videoId: "${buttonConfig.redirectVideoId}"}}, "*")
    }`
    buttonElement = `<button class="buttonConfig" onclick="play();redirect()"><b>${buttonConfig.title}</b></button>`
  } else {
    // Para link externo
    redirectFunction = `function redirect(){
      window.open('${buttonConfig.link || ''}', '_blank')
    }`
    buttonElement = `<a class="buttonConfig" target="_blank" href="${
      buttonConfig.link || 'https://'
    }"><b>${buttonConfig.title}</b></a>`
  }

  const generatedScript =
    `<script>function play(){window.parent.postMessage({type: 'cta_play',}, '*')};
    ${redirectFunction};
    document.getElementById("button-container").addEventListener("click", function(e) {if (e.target == this) {window.parent.postMessage({type: 'play_toggle',}, '*')}})
    </scr` +
    `ipt><style> 
    #button-container {
      position: relative;
      z-index: 9999;
      pointer-events: auto;
    }
    .buttonConfig { 
      cursor:pointer; 
      ${scriptMargin}; 
      background-color:${buttonConfig.backgroundColor};
      color:${buttonConfig.textColor}; 
      ${btnMeasurements}; 
      border:none; 
      border-radius:10px; 
      text-decoration: none; 
      font-style: normal;
      font-family: 'Rubik', sans-serif;
      position: relative;
      z-index: 10000;
    }
    .buttonConfig:hover {
      background-color:${buttonConfig.backgroundHover};
      color:${buttonConfig.textHover}
    }
${btnQueries ?? ''}
    </style>
    <div id="button-container" style="display:flex;${btnAlignment};height:100%;">
  
    ${buttonElement}</div>
    
    `
  return generatedScript
}

/**
 * Gera os metadados padrão para um botão CTA
 * @returns {Object} Metadados padrão do botão
 */
export function getDefaultButtonMetadata() {
  return {
    title: i18next.t('message.pages.video.buttonsPage.accessHere'),
    link: '',
    selectedButtonType: 'insideVideo',
    textColor: '#FFFFFF',
    textHover: '#FFFFFF',
    backgroundColor: '#3E50F7',
    backgroundHover: '#3E50F7',
    selectedButtonSize: 'small',
    selectedButtonPlacement: 'bottomCenter',
    ctaType: 'button',
    isCustomHtml: false,
    codeHtml: '',
    lastAllVideo: false,
    lastTillEnd: false,
    keepWindow: false,
    appendWebsiteQueryParams: false,
    clickBehavior: 'external_link',
    redirectVideoId: '',
    videoId: '',
  }
}

/**
 * Cria um item de botão CTA completo para a timeline
 * @param {Object} existingData - Dados existentes do item (opcional)
 * @param {number} startTimeSeconds - Tempo inicial em segundos
 * @param {number} duration - Duração em segundos
 * @returns {Object} Item completo do botão CTA
 */
export function createButtonCtaItem(
  existingData = {},
  startTimeSeconds = 0,
  duration = 10
) {
  const newStartTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds)
  )
  const newEndTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds + duration)
  )

  const startFormat = newStartTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'
  const endFormat = newEndTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'

  const metadata = {
    ...getDefaultButtonMetadata(),
    ...(existingData.metadata || {}),
    startTime: format(newStartTime, startFormat),
    endTime: format(newEndTime, endFormat),
  }

  const data = {
    ...existingData,
    metadata,
    duration,
    time: startTimeSeconds,
    id: existingData.id || v4(),
    script: generateInsideButtonScript(metadata),
    title: metadata.title,
    type: 'video',
    maxZIndex: true,
  }

  return data
}

/**
 * Gera o script HTML/CSS/JS para um quiz/pergunta CTA
 * @param {Object} quizConfig - Configuração do quiz
 * @returns {string} Script gerado para o player
 */
export function generateQuizScript(quizConfig) {
  const { questions, id, startTime, endTime } = quizConfig.metadata

  return `
                    <style>
      @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap');
      body {
        font-family: 'Rubik', sans-serif;
      }

      h2, .questionText, .answers button, .answers a {
        font-family: 'Rubik', sans-serif;
      }
html {
overflow: hidden;
}

@keyframes slideInProgress {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    ;
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes topSlideIn {
  from {
    ;
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    ;
    opacity: 0;
  }
}

@keyframes bottomSlideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    ;
    opacity: 0;
  }
}
@keyframes slideInButtons {
  0% {
;
    opacity: 0;
  }
  80% {
      ;
    opacity: 1;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}

.slide-in {
  animation: slideIn 0.5s ease-out forwards;
}

.top-slide-in {
  animation: topSlideIn 0.5s ease-out forwards;
}

.progress-slide-in {
  animation: slideInProgress 0.5s ease-out forwards;
}

.button-slide-in {
  animation: slideInProgress 0.5s ease-out forwards;
}

.slide-out {
  animation: slideOut 0.5s ease-in forwards;
}

.bottom-slide-out {
  animation: bottomSlideOut 0.5s ease-in forwards;
}

#video-container{
width: 100%;
height: 100%;
  display: grid; grid-template-rows: auto 1fr; position: relative;;
    ;

}
                      #container{
                        padding: 12px 0;
                        display: flex;
                        background-color: rgba(0, 0, 0, 1);
                        font-family: 'Rubik', sans-serif;
                        width: 100%;
                        position: absolute; ;
                          ;
                            ;

                      }
                        #container2{
                          width: 100%; position: absolute; bottom: 0; display: flex;;
                           left: 0;
                        background-color: rgba(0, 0, 0, 1);
                        font-family: 'Rubik', sans-serif;
                        padding-top: 5px;
                      }
                        #container2 button {
                        animation: slideInButtons 1s ease-out forwards;
  opacity: 0;
}
  #container2 button:nth-child(1) {
  animation-delay: 0.6s;
}

#container2 button:nth-child(2) {
  animation-delay: 1s;
}

#container2 button:nth-child(3) {
  animation-delay: 1.4s;
}

.side-section {
    ;
     left: 0;;
}

.progress-container {
  height: 5px;
    background-color:rgba(0, 0, 0, 1);
  border-radius: 100px;
  z-index: 10;
  overflow: hidden;
  opacity: 1;
  transition: visibility 0s linear 2s, opacity 0s linear 2s;
  position: absolute;  width: 100%; bottom: 80px;
  ;
  ;
}
  @keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

  .progress-bar {
    height: 100%;
    border-radius: 100px;
    transition: transform 0.5s ease-in-out;
    transform: scaleX(0);
    will-change:  transform;
    background: transparent;
  }
  }

                      h2{
                        color: #5F2EEA;
                        font-size: 1.5rem;
                        font-weight: 500;
                      }
                      .question{
                        margin: auto !important;
                        display: flex !important;
                        flex-direction: column !important;
                        max-width: 500px !important;
                        position: static !important;
                        bottom: unset !important; 
                        left: unset !important;
                        transform: none !important; 
                        
                      }
                      .answers{
                        display: flex; gap: 24px;;
                        margin: 20px 0;
                        gap: 20px;

                      }
                      .answer-button{
                        font-size: 1.2rem;
                        color: #5F2EEA;
                        background-color: transparent;
                        border-radius: 10px;
                        padding: 12px 15px;
                        border: 1px solid #5F2EEA;
                        margin-top: 10px;
                        cursor: pointer;
                      }
.default {
  border: none;
  padding: 10px var(--Box-Margem-vertical, 24px);
  min-height: 40px;
  justify-content: center;
  background-color: #5F2EEA;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}
.default:hover {
background-color: #774DED;
    }

    .losangle {
    width: 100% !important;
    cursor: pointer !important;
    background-color: transparent;
    border: none;
    text-align: center;
    leading-trim: both;
    text-edge: cap;
    font-family: Rubik;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    color: #FFFFFF;
    }

.dimension {
  border: none;
  background-color: #5F2EEA;
  padding: 10px var(--Box-Margem-vertical, 24px);
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: #fff;
  text-align: center;
  min-height: 40px;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  align-self: stretch;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 4px 4px 8px 0px rgba(0, 0, 0, 0.25) inset,
    -4px -4px 8px 0px rgba(255, 255, 255, 0.25) inset;
}
.dimension:hover {
  background-color: #774DED;
}
.contour {
  border: 2px solid #5F2EEA;
  background-color: transparent;
  padding: 4px var(--Box-Margem-vertical, 24px);
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  min-height: 40px;
  cursor: pointer;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
      color: #FFFFFF;
}

                      .questionText{
 color: ${quizConfig.metadata.generalData.questionTextColor?.color || '#FFF'};
 text-align: center;
 font-size: 16px;
 font-style: normal;
 font-weight: 700;
 line-height: normal;
 word-wrap: break-word;
    max-width: 100%;
  white-space: normal; /
}

                      .answers a{
                        font-size: 1.2rem;
                        text-decoration: none;
                        color: #5F2EEA;
                        background-color: rgba(0,0,0,0.8);
                        border-radius: 10px;
                        padding: 12px 15px;
                        border: 1px solid #5F2EEA;
                        margin-top: 10px;
                        cursor: pointer;
                      }
                      #skip-button{
                        position: absolute;
                        z-index: 100;
                        bottom: 30px;
                        left: 30px;
                        background-color: rgba(255,255,255,0.1);
                        color: white;
                        padding: 12px 22px;
                        font-size: 12px;
                        border: none;
                        border-radius: 8px;
                        font-weight: 600;
                        cursor: pointer;
                        outline: none;
                      }
                      @media (max-width: 550px){
                        #container2 {
                          height: 60px;
                        }
                         #skip-button{
                         bottom: 70px;
                         right: 20px; left: auto
                         }
                         #container {
                         bottom: 60px;                   
                           
                         }    
                        .dimension {
                        min-height: 30px;
                        padding: 6px 12px;
                        }
                        .answers {
                         gap: 12px;
                        }
                        .default {
                        min-height: 30px;
                        padding: 6px 12px;
                        }
                        .contour {
                        min-height: 30px;
                        padding: 6px 12px;
                        }
                        .questionText {
                         font-size: 10px;
                        }  
                        .progress-container {
                          bottom: 55px;
                        }
                        h2 {
                          font-size: 10px;
                        }
                        .answers button{
                          font-size: 9px;
                          margin-top: -5px;
                        }
                      }

                    </style>
                    <div id="video-container" class="click-area">
                    <div class="side-section">
                    <div id="container" class="top-slide-in">
                    </div>
                    <div                           ; style="background: rgba(0, 0, 0, 1);; display: flex; justify-content: center;" id="progressContainer" >
                    </div>
                    <div id="container2" class="slide-in">
                    </div>
                     </div>
                     </div>
                    <button id="skip-button" onclick="skipQuestion()">${i18next.t(
                      'message.quiz.skip'
                    )}</button>
                    <script> 
                      document.querySelector('.click-area').addEventListener('click', function(event) {
                        if (event.target === this) {
                        }
                      });

                      const quizData = ${JSON.stringify(questions || [])};
                      let currentQuestionIndex = 0;
                      
                      const originalStartTime = '${startTime}';
                      const effectiveStartTime = originalStartTime === '00:00' ? '00:01' : originalStartTime;

                      function onClickAnswer(questionIndex, answerIndex){
                        const question = quizData[questionIndex];
                        const answer = question.answers[answerIndex];
                        if(answer.action.type === 'open_link' || answer.action.type === 'no_action'){
                          setTimeout(() => {
                          window.parent.postMessage({
                            type: 'remove_cta',
                            parameter: '${id}',
                          }, '*');
                          }, 500);
                          return;
                        }
                        if(answer.action.type === 'redirect_video'){
                          window.parent.postMessage({
                            type: 'redirect_video',
                            parameter: {
                              videoId: answer.action.videoExternalId
                            }
                          }, '*');
                        }
                        const isLastQuestion = currentQuestionIndex === quizData.length - 1;
                        if (answer.action.type === 'redirect_question') {
                        const targetQuestionIndex = answer.action.nextQuestion;
                        const nextQuestion = quizData[targetQuestionIndex];
                        renderQuestion(nextQuestion);
                        }

                        if(isLastQuestion && answer.action.type !== 'redirect_question'){
                          const container = document.getElementById('container');
                          const container2 = document.getElementById('container2');
                          const progressBar = document.getElementById('progressContainer');

                          container.classList.add('slide-out');
                          container2.classList.add('bottom-slide-out');
                          progressBar.classList.add('slide-out');
                          setTimeout(() => {
                          window.parent.postMessage({
                            type: 'remove_cta',
                            parameter: '${id}',
                          }, '*');
                          }, 500);
                        }
                      }
                      function skipQuestion(){
                                                setTimeout(() => {
                          window.parent.postMessage({
                            type: 'remove_cta',
                            parameter: '${id}'
                          }, '*');
                         }, 500);
                          return;
                      }
                        function timeToSeconds(time) {
                         const [minutes, seconds] = time.split(':').map(Number);
                         return minutes * 60 + seconds;
                      }
                      function renderQuestion(question){
                        const existentQuestion = document.querySelector('.question');
                        if(existentQuestion){
                          existentQuestion.remove();
                        }
                        const questionIndex = quizData.indexOf(question);
                        currentQuestionIndex = questionIndex;
                        const isLastQuestion = questionIndex === quizData.length - 1;

                        let currentTime = 0;
                        try {
                          currentTime = window.parent.player.media.plyr.thumbnails.lastTime || 0;
                        } catch (e) {
                          currentTime = 0;
                        }
                        let timeLeft = 10;
                        const totalTime = 10;
                        const startTime = timeToSeconds(effectiveStartTime);
                        const endTime = timeToSeconds('${endTime}');

                        function updateProgressBar() {
                          try {
                            const currentTime = window.parent.player.media.plyr.thumbnails.lastTime;

                            if (currentTime >= startTime && currentTime <= endTime) {
                              const elapsedTime = currentTime - startTime;
                              const totalTime = endTime - startTime;
                              const progressPercentage = (elapsedTime / totalTime);
                              const progressBar = document.querySelector('.progress-bar');
                              if (progressBar) {
                                progressBar.style.transform = 'scaleX(' + progressPercentage + ')';
                              }
                            }
                          } catch (e) {
                            // Player not ready yet, skip this frame
                          }

                          requestAnimationFrame(updateProgressBar);
                        }

                        requestAnimationFrame(updateProgressBar);                    
                        const windowWidth = window.innerWidth;
                        const answersHtml = \`
                        <div class="question">
                         <span class="questionText"> \${question.question} </span>
                          </div>
                        \`;
                        const progressBarContainer = \`
                             <div class="progress-container">
                             <div class="progress-bar"></div>
                        </div>
                        \`;
                        const questionsHtml = \`
                        <div class="question">
                          <div class="answers">
                            \${question.answers.map((answer, answerIndex)=>answer.action.type==='open_link'?\`
                            <button class="default" onclick="window.open('\${answer.action.link}', '_blank'); onClickAnswer(\${questionIndex}, \${answerIndex})" target="_blank">
                            \${answer.text}
                              <div> 
                               </div>
                            </button>
                            \`:\`
                            <button class="default" onclick="onClickAnswer(\${questionIndex}, \${answerIndex})" style="max-width: 100%; white-space: normal; word-wrap: break-word;">\${answer.text}
                              <div> 
                              </div>
                            </button>
                            \`).join('')}
                          </div>
                        \`;
                        const container = document.getElementById('container');
                        const container2 = document.getElementById('container2');
                        const progressContainer = document.getElementById('progressContainer');
                        
                        if (!container || !container2 || !progressContainer) {
                          console.error('Quiz containers not found in DOM');
                          return;
                        }
                        
                        container.innerHTML = answersHtml;
                        container2.innerHTML = questionsHtml;
                        progressContainer.innerHTML = progressBarContainer;
                        
                        const progressBar = document.querySelector('.progress-bar');
                        if (question.question.length === 0 && 0 === 0) {
                          container.style.removeProperty('display');
                          container.style.display = 'none';
                        } else {
                          container.style.display = 'flex';
                        }
                      }
                      
                      // Initialize quiz (treat 00:00 as 00:01 internally)
                      renderQuestion(quizData[0]);


                    </script>
                  `
}

/**
 * Gera os metadados padrão para um quiz CTA
 * @returns {Object} Metadados padrão do quiz
 */
export function getDefaultQuizMetadata() {
  return {
    generalData: {
      questions: {
        styleText: '',
        layout: 0,
        horizontalLayout: 0,
        verticalLayout: 0,
        alternativesSelectedBackgroundColor: { background: '#000000' },
        selectedExitAnimation: '',
        selectedEnterAnimation: '',
        pauseVideo: true,
        pauseVideoEnd: false,
        hasTimeBar: false,
        selectedButtonModel: 'default',
        selectedColor: { fill: '#5F2EEA', path: '#774DED' },
        allowSkip: true,
        skipButtonText: i18next.t('message.quiz.skip'),
        fontSize: 16,
        fontColor: { color: '#FFFFFF' },
        questionTextColor: { color: '#FFFFFF' },
        fontWeight: 'normal',
        availableFontWeights: [
          { name: 'Normal', value: 'normal' },
          { name: 'Bold', value: 'bold' },
          { name: 'Bolder', value: 'bolder' },
          { name: 'Lighter', value: 'lighter' },
        ],
      },
      background: {
        color: { color: '#000000', opacity: 100, alternativesOpacity: 100 },
      },
      startTime: '00:00',
      endTime: '00:10',
      duration: 10,
      id: '',
    },
    disposition: 0,
    questionDisposition: 0,
    verticalLayout: 0,
    questions: [
      {
        statement: { value: i18next.t('message.quiz.defaultQuestion') },
        answers: [
          {
            text: i18next.t('message.quiz.defaultAnswer') + ' A',
            action: 'no_action',
          },
          {
            text: i18next.t('message.quiz.defaultAnswer') + ' B',
            action: 'no_action',
          },
        ],
        index: 0,
      },
    ],
    ctaType: 'quiz',
    startTime: '00:00',
    endTime: '00:10',
    redirects: [],
    videoRedirectExternalIds: [],
  }
}

export function createDefaultQuestion(index) {
  return {
    statement: { value: i18next.t('message.quiz.defaultQuestion') },
    answers: [
      {
        text: i18next.t('message.quiz.answerA'),
        action: 'no_action',
      },
    ],
    index,
  }
}

/**
 * Cria um item de quiz CTA completo para a timeline
 * @param {Object} existingData - Dados existentes do item (opcional)
 * @param {number} startTimeSeconds - Tempo inicial em segundos
 * @param {number} duration - Duração em segundos
 * @returns {Object} Item completo do quiz CTA
 */
export function createQuizCtaItem(
  existingData = {},
  startTimeSeconds = 0,
  duration = 10
) {
  const newStartTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds)
  )
  const newEndTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds + duration)
  )

  const startFormat = newStartTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'
  const endFormat = newEndTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'

  const id = existingData.id || v4()

  const metadata = {
    ...getDefaultQuizMetadata(),
    ...(existingData.metadata || {}),
    startTime: format(newStartTime, startFormat),
    endTime: format(newEndTime, endFormat),
    generalData: {
      ...getDefaultQuizMetadata().generalData,
      ...(existingData.metadata?.generalData || {}),
      startTime: format(newStartTime, startFormat),
      endTime: format(newEndTime, endFormat),
      duration,
      id,
    },
  }

  const data = {
    ...existingData,
    metadata,
    type: 'video',
    time: startTimeSeconds,
    duration,
    id,
    script: generateQuizScript({ metadata }),
    maxZIndex: true,
    pauseVideo: true,
    pauseVideoEnd: false,
    barColor: '#5F2EEA',
    hasTimeBar: false,
    alternativesSelectedBackgroundColor: { background: '#000000' },
    selectedButtonModel: 'default',
    buttonColor: { fill: '#5F2EEA', path: '#774DED' },
    allowSkip: true,
  }

  return data
}

/**
 * Gera o script HTML/CSS/JS para um formulário CTA
 * @param {Object} formConfig - Configuração do formulário
 * @returns {string} Script gerado para o player
 */
export function generateFormScript(formConfig) {
  const {
    textFields,
    sendButton,
    headline,
    subHeadline,
    general,
    background,
    id,
  } = formConfig.metadata.generalData

  const hexToRgba = (hex, opacity) => {
    const hexColor = hex.replace('#', '')
    const r = parseInt(hexColor.substring(0, 2), 16)
    const g = parseInt(hexColor.substring(2, 4), 16)
    const b = parseInt(hexColor.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`
  }

  return `
                    <style>
                      @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap');
                      
                      #container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        height: 100%;
                        width: 100%;
                        background: ${hexToRgba(
                          background.color,
                          background.opacity
                        )};
                        font-size: ${headline.fontSize}px;
                        font-family: 'Rubik', sans-serif;
                      }
                      #content {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        width: 100%;
                        padding: 20px;
                        max-width: 400px;
                      }
                      .name-form {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                      }
                      .form-input label {
                        margin-bottom: 3px;
                        font-size: 14px;
                        color: #FFFFFF;
                        font-family: 'Rubik', sans-serif;
                      }
                      .form-input {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                      }
                      h1{
                        font-size: ${headline.fontSize}px;
                        color: ${headline.color};
                        margin: 0;
                        font-family: 'Rubik', sans-serif;
                      }
                      h2{
                        font-size: ${subHeadline.fontSize}px;
                        color: ${subHeadline.color};
                        margin: 15px 0;
                        font-weight: 400;
                        font-family: 'Rubik', sans-serif;
                      }
                      input {
                        padding: 10px;
                        border-radius: 4px;
                        width: 100%;
                        border: 1px solid #EDEEF1;
                        background: #FFFFFF;
                        outline: none;
                        color: #282931;
                        font-family: 'Rubik', sans-serif;
                      }
                      button.send-button {
                        padding: 10px 20px;
                        width: 100%;
                        border-radius: 5px;
                        border: none;
                        background: ${sendButton.backgroundColor};
                        color: ${sendButton.color};
                        cursor: pointer;
                        font-size: 14px;
                        font-family: 'Rubik', sans-serif;
                      }
                      #skip-button{
                        background-color: #EDEEF1;
                        color: #3F404C;
                        padding: 10px 20px;
                        font-size: 14px;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        outline: none;
                        margin-right: 10px;
                        font-family: 'Rubik', sans-serif;
                      }
                      button.send-button:hover {
                        background: ${sendButton.backgroundColorHover};
                        color: ${sendButton.colorHover};
                      }
                      .send-button-container{
                        display: flex;
                        width: 100%;
                        margin-top: 10px;
                        justify-content: center;
                        align-items: center;
                      }

                    </style>
                    <div id="container">
                      <div id="content">
                        <h1>${headline.text}</h1>
                        <h2>${subHeadline.text}</h2>
                        <div class="name-form" >

                          ${
                            general.showNameField
                              ? `
                            <div class="form-input">
                              <label for="name">${textFields.name}</label>
                              <input type="text" id="name" name="name">
                            </div>
                            `
                              : ''
                          }
                          ${
                            general.showLastNameField && general.showNameField
                              ? `
                              <div style="width: 30px;">
                              </div>
                            `
                              : ''
                          }
                          ${
                            general.showLastNameField
                              ? `
                            <div class="form-input">
                              <label for="lastName">${textFields.lastName}</label>
                              <input type="text" id="lastName" name="lastName">
                            </div>
                          `
                              : ''
                          }
                        </div>
                        <div class="form-input" style="margin-top: 8px;">
                          <label for="email">${textFields.email}</label>
                          <input type="text" id="email" name="email">
                        </div>
                        <div class="send-button-container">
                          ${
                            general.allowSkip
                              ? '<button id="skip-button">Skip</button>'
                              : ''
                          }
                          <button class="send-button" id="send-button">${
                            sendButton.text
                          }</button>
                        </div>
                      </div>
                    </div>

                    <script>
                      function handleSendData(){
                        const dataToSend = {};
                        if(${general.showNameField}){
                          dataToSend.name = document.getElementById('name').value;
                        }
                        if(${general.showLastNameField}){
                          if(!dataToSend.name) dataToSend.name='';
                          dataToSend.name += ' '+document.getElementById('lastName').value;
                        }
                        dataToSend.video_name = 'Vídeo para Status 30 segundos..mp4'
                        dataToSend.email = document.getElementById('email').value;
                        window.parent.postMessage({
                          type: 'send_form',
                          parameter: dataToSend
                        }, '*');
                        window.parent.postMessage({
                          type: 'remove_cta',
                          parameter: '${id}'
                        }, '*');
                      }
                      let sendButton = document.getElementById('send-button');
                      sendButton.addEventListener('click', handleSendData);
                      document.addEventListener('keydown', function(event) {
                        if (event.key === 'Enter') {
                         handleSendData();
                        }
                      });
                      if(${general.allowSkip}){
                        let skipButton = document.getElementById('skip-button');
                        skipButton.addEventListener('click', () => {
                          window.parent.postMessage({
                            type: 'remove_cta',
                            parameter: '${id}'
                          }, '*');
                        });
                      }

                    </script>
                  `
}

/**
 * Gera os metadados padrão para um formulário CTA
 * @returns {Object} Metadados padrão do formulário
 */
export function getDefaultFormMetadata() {
  return {
    generalData: {
      textFields: {
        name: i18next.t('message.pages.integrations.nameText'),
        lastName: i18next.t('message.pages.integrations.lastNameText'),
        email: i18next.t('message.pages.integrations.emailText'),
        invalidEmailText: i18next.t(
          'message.pages.integrations.invalidEmailText'
        ),
      },
      background: {
        color: '#000000',
        opacity: 65,
      },
      sendButton: {
        text: i18next.t('message.pages.integrations.textButtonText'),
        color: '#FFFFFF',
        colorHover: '#FFFFFF',
        backgroundColor: '#3F55EE',
        backgroundColorHover: '#000000',
      },
      headline: {
        text: i18next.t('message.pages.integrations.headlineText'),
        fontSize: 16,
        color: '#FFFFFF',
      },
      subHeadline: {
        text: i18next.t('message.pages.integrations.subHeadlineText'),
        fontSize: 16,
        color: '#FFFFFF',
      },
      integrations: [],
      startTime: '00:00',
      duration: 10,
      id: '',
      pauseVideo: false,
      pauseVideoEnd: false,
      general: {
        showNameField: true,
        showLastNameField: true,
        allowSkip: true,
      },
      endTime: '00:10',
    },
    ctaType: 'form',
    startTime: '00:00',
    endTime: '00:10',
  }
}

/**
 * Cria um item de formulário CTA completo para a timeline
 * @param {Object} existingData - Dados existentes do item (opcional)
 * @param {number} startTimeSeconds - Tempo inicial em segundos
 * @param {number} duration - Duração em segundos
 * @returns {Object} Item completo do formulário CTA
 */
export function createFormCtaItem(
  existingData = {},
  startTimeSeconds = 0,
  duration = 10
) {
  const newStartTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds)
  )
  const newEndTime = new Date(
    new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(startTimeSeconds + duration)
  )

  const startFormat = newStartTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'
  const endFormat = newEndTime.getHours() > 0 ? 'HH:mm:ss' : 'mm:ss'

  const id = existingData.id || v4()

  const metadata = {
    ...getDefaultFormMetadata(),
    ...(existingData.metadata || {}),
    startTime: format(newStartTime, startFormat),
    endTime: format(newEndTime, endFormat),
    generalData: {
      ...getDefaultFormMetadata().generalData,
      ...(existingData.metadata?.generalData || {}),
      startTime: format(newStartTime, startFormat),
      endTime: format(newEndTime, endFormat),
      duration,
      id,
    },
  }

  const data = {
    ...existingData,
    metadata,
    type: 'video',
    time: startTimeSeconds,
    duration,
    id,
    script: generateFormScript({ metadata }),
    pauseVideo: false,
    pauseVideoEnd: false,
    maxZIndex: true,
  }

  return data
}

/**
 * Utilitário para converter time string para segundos
 * @param {string} timeStr - String no formato HH:mm:ss ou mm:ss
 * @returns {number} Tempo em segundos
 */
export function timeToSeconds(timeStr) {
  const parts = timeStr.split(':')

  if (parts.length === 3) {
    const hours = parseFloat(parts[0], 10)
    const minutes = parseFloat(parts[1], 10)
    const seconds = parseFloat(parts[2], 10)
    return hours * 3600 + minutes * 60 + seconds
  } else if (parts.length === 2) {
    const minutes = parseFloat(parts[0], 10)
    const seconds = parseFloat(parts[1], 10)
    return minutes * 60 + seconds
  } else {
    throw new Error('Invalid time format')
  }
}
